<template>
  <b-form
    class="form__post-response"
    :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
    :data-editing="isEditing"
    @submit.prevent="onSubmit"
  >
    <div v-if="criteria.length">
      <div v-if="mode !== 'assess'" class="mb-3">
        <b>Evaluation</b>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-4">
        <div v-if="!giveFeedback && !readonly && allowAssess && mode !== 'assess'">
          <b-button
            variant="outline-primary"
            :class="{
              'button__give-feedback': !selected,
              'button__edit-feedback': selected
            }"
            :disabled="evidenceSubmitting"
            :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
            @click="onGiveFeedback"
            ><b-spinner v-if="isLoadingPermission" small /><span v-else>{{
              selected ? 'Edit' : 'Give feedback'
            }}</span></b-button
          >
        </div>
        <div v-else-if="mode === 'assess'">
          <b>Evaluation</b>
          <div v-if="isAIDisabledByPartDependency" style="font-size: 14px; font-weight: 400; color: #F83245;">
            AI generate is unavailable until parts 1 and 2 are complete.
          </div>
        </div>
        <div v-else />

        <b-button
          v-if="!readonly && allowAssess && mode === 'assess'"
          :class="aiGenerated ? 'button__regenerate-ai' : 'button__generate-ai'"
          :disabled="evidenceSubmitting || generating || isAIDisabledByPartDependency"
          @click="onGenerateWithAI"
        >
          <div v-if="generating" class="d-flex align-items-center">
            <b-spinner small />
            <span class="ml-2">Generating...</span>
          </div>
          <div v-else class="d-flex align-items-center">
            <!-- Wand Icon -->
            <svg
              v-if="!aiGenerated"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 640 640"
              fill="currentColor"
              class="icon__ai-btn"
            >
              <path
                d="M295.4 37L310.2 73.8L347 88.6C350 89.8 352 92.8 352 96C352 99.2 350 102.2 347 103.4L310.2 118.2L295.4 155C294.2 158 291.2 160 288 160C284.8 160 281.8 158 280.6 155L265.8 118.2L229 103.4C226 102.2 224 99.2 224 96C224 92.8 226 89.8 229 88.6L265.8 73.8L280.6 37C281.8 34 284.8 32 288 32C291.2 32 294.2 34 295.4 37zM142.7 105.7L164.2 155.8L214.3 177.3C220.2 179.8 224 185.6 224 192C224 198.4 220.2 204.2 214.3 206.7L164.2 228.2L142.7 278.3C140.2 284.2 134.4 288 128 288C121.6 288 115.8 284.2 113.3 278.3L91.8 228.2L41.7 206.7C35.8 204.2 32 198.4 32 192C32 185.6 35.8 179.8 41.7 177.3L91.8 155.8L113.3 105.7C115.8 99.8 121.6 96 128 96C134.4 96 140.2 99.8 142.7 105.7zM496 368C502.4 368 508.2 371.8 510.7 377.7L532.2 427.8L582.3 449.3C588.2 451.8 592 457.6 592 464C592 470.4 588.2 476.2 582.3 478.7L532.2 500.2L510.7 550.3C508.2 556.2 502.4 560 496 560C489.6 560 483.8 556.2 481.3 550.3L459.8 500.2L409.7 478.7C403.8 476.2 400 470.4 400 464C400 457.6 403.8 451.8 409.7 449.3L459.8 427.8L481.3 377.7C483.8 371.8 489.6 368 496 368zM492 64C503 64 513.6 68.4 521.5 76.2L563.8 118.5C571.6 126.4 576 137 576 148C576 159 571.6 169.6 563.8 177.5L475.6 265.7L374.3 164.4L462.5 76.2C470.4 68.4 481 64 492 64zM76.2 462.5L340.4 198.3L441.7 299.6L177.5 563.8C169.6 571.6 159 576 148 576C137 576 126.4 571.6 118.5 563.8L76.2 521.5C68.4 513.6 64 503 64 492C64 481 68.4 470.4 76.2 462.5z"
              ></path>
            </svg>
            <!-- Loop Right Icon -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="icon__ai-btn"
            >
              <path
                d="M12 4C14.5905 4 16.8939 5.23053 18.3573 7.14274L16 9.5H22V3.5L19.7814 5.71863C17.9494 3.452 15.1444 2 12 2 6.47715 2 2 6.47715 2 12H4C4 7.58172 7.58172 4 12 4ZM20 12C20 16.4183 16.4183 20 12 20 9.40951 20 7.10605 18.7695 5.64274 16.8573L8 14.5 2 14.5V20.5L4.21863 18.2814C6.05062 20.548 8.85557 22 12 22 17.5228 22 22 17.5228 22 12H20Z"
              ></path>
            </svg>
            <span>{{ aiGenerated ? 'Regenerate' : 'Generate with AI' }}</span>
          </div>
        </b-button>
      </div>
      <criteria-radio-group
        v-for="criterion in criteria"
        :key="criterion.id"
        v-model="selected"
        :criterion="criterion"
        :give-feedback="giveFeedback && !readonly"
        :formated-part="formatedPart"
        :order="order"
        :show-all-criteria="true"
        @radio-change="onRadioChange"
      />
    </div>

    <div v-if="!hideComment">
      <div class="d-flex justify-content-between">
        <label for="description" class="header__title d-flex align-items-center">
          <b>{{ headerTitle }}&nbsp;</b>

          <span
            v-if="headerTitle === 'Evidence' && !readonly"
            class="text-black font-weight-normal"
          >
            (Enter your answer here or upload a file.)
          </span>

          <span v-if="isAIGenerated" class="badge__ai-generated ml-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 640 640"
              fill="currentColor"
              class="icon__ai-badge"
            >
              <path
                d="M295.4 37L310.2 73.8L347 88.6C350 89.8 352 92.8 352 96C352 99.2 350 102.2 347 103.4L310.2 118.2L295.4 155C294.2 158 291.2 160 288 160C284.8 160 281.8 158 280.6 155L265.8 118.2L229 103.4C226 102.2 224 99.2 224 96C224 92.8 226 89.8 229 88.6L265.8 73.8L280.6 37C281.8 34 284.8 32 288 32C291.2 32 294.2 34 295.4 37zM142.7 105.7L164.2 155.8L214.3 177.3C220.2 179.8 224 185.6 224 192C224 198.4 220.2 204.2 214.3 206.7L164.2 228.2L142.7 278.3C140.2 284.2 134.4 288 128 288C121.6 288 115.8 284.2 113.3 278.3L91.8 228.2L41.7 206.7C35.8 204.2 32 198.4 32 192C32 185.6 35.8 179.8 41.7 177.3L91.8 155.8L113.3 105.7C115.8 99.8 121.6 96 128 96C134.4 96 140.2 99.8 142.7 105.7zM496 368C502.4 368 508.2 371.8 510.7 377.7L532.2 427.8L582.3 449.3C588.2 451.8 592 457.6 592 464C592 470.4 588.2 476.2 582.3 478.7L532.2 500.2L510.7 550.3C508.2 556.2 502.4 560 496 560C489.6 560 483.8 556.2 481.3 550.3L459.8 500.2L409.7 478.7C403.8 476.2 400 470.4 400 464C400 457.6 403.8 451.8 409.7 449.3L459.8 427.8L481.3 377.7C483.8 371.8 489.6 368 496 368zM492 64C503 64 513.6 68.4 521.5 76.2L563.8 118.5C571.6 126.4 576 137 576 148C576 159 571.6 169.6 563.8 177.5L475.6 265.7L374.3 164.4L462.5 76.2C470.4 68.4 481 64 492 64zM76.2 462.5L340.4 198.3L441.7 299.6L177.5 563.8C169.6 571.6 159 576 148 576C137 576 126.4 571.6 118.5 563.8L76.2 521.5C68.4 513.6 64 503 64 492C64 481 68.4 470.4 76.2 462.5z"
              ></path>
            </svg>
            AI Generated (editable)
          </span>

          <!-- <span v-if="!readonly && post.word_limit && mode !== 'review'" class="text-black-50"
            >(Please keep response to {{ post.word_limit }} words.)</span
          > -->
        </label>

        <transition name="slide-fade-right">
          <div v-show="!readonly && saveMessage.length">
            <span class="p-2 mr-2">
              <VueElementLoading :active="saving" size="20" color="#3b3e66" />
            </span>

            <span class="text-black-50">{{ saveMessage }}</span>

            <b-button v-if="savingError" variant="link" size="sm" @click.prevent="retrySave"
              >Retry</b-button
            >
          </div>
        </transition>
      </div>

      <!-- <small v-if="!readonly" class="text-black-50">
        Word count: {{ countTextDescription }}
      </small> -->

      <div v-if="readonly" class="response__content">
        <base-text-editor
          v-model="formDescription"
          class="readonly text__feedback-text"
          :show-toolbar="false"
          :disabled="true"
          :drop-image="false"
        >
        </base-text-editor>
      </div>
      <div v-else @click="mode === 'assess' ? onEditorFocus() : null">


        <base-text-editor
          :ref="{
            'input__feedback-textfield': giveFeedback,
            'text__feedback-text': !giveFeedback
          }"
          v-model="formDescription"
          name="input__feedback"
          :class="{
            text_editor__required: onChanges && !formDescription,
            'input__feedback-textfield': giveFeedback,
            'text__feedback-text': !giveFeedback,
            'text-editor__ai-highlight': aiHighlight
          }"
          :show-toolbar="(['review', 'assess'].includes(mode)) === allowAssess"
          :disabled="['review', 'assess'].includes(mode) ? disabled || !giveFeedback : disabled || !isEditing"
          :amplitude-attribute="{ Part: part, TaskNo: order }"
          :placeholder="(['review', 'assess'].includes(mode)) === allowAssess ? 'Enter your comment here...' : ''"
          @click.native="mode === 'assess' ? onEditorFocus() : null"
          @focus="mode === 'assess' ? onEditorFocus() : null"
          @change="mode === 'assess' ? onEditorFocus() : null"
        ></base-text-editor>
      </div>
    </div>

    <div
      v-if="!readonly && !['review', 'assess'].includes(mode)"
      class="mt-3 d-flex align-items-center justify-content-between"
    >
      <b-button
        variant="outline-dark"
        class="mb-3"
        :disabled="['review', 'assess'].includes(mode) ? disabled || !giveFeedback : disabled || !isEditing"
        :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
        @click="openFileBrowser"
      >
        <FontAwesomeIcon icon="paperclip" class="btn__icon-paperclip mr-2" />
        Choose files
      </b-button>
    </div>

    <div v-if="giveFeedback && !readonly && ['review', 'assess'].includes(mode)" class="mt-3">
      <b-button
        type="submit"
        variant="success"
        class="button__save-changes"
        :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
        :disabled="mode === 'assess' ? isAssessorSaveDisabled : changing"
      >
        <b-spinner v-if="changing" small /><span v-else>Save changes</span>
      </b-button>

      <b-button
        v-if="mode !== 'assess'"
        variant="link"
        :disabled="changing"
        :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
        @click="onClickCancel"
        >Cancel</b-button
      >
    </div>

    <div v-if="!['review', 'assess'].includes(mode)" class="mt-3 form__post-response-attachments">
      <base-files-list
        v-show="readonly || !isEditing"
        :attachments="formAttachments"
      ></base-files-list>

      <base-s3-uploader
        v-show="isEditing"
        :id="`inputAttachments_${post.id}`"
        ref="attachmentUploader"
        v-model="formAttachments"
        :reference-meta="{ ...referenceMeta, field: 'attachments' }"
        :max-filesize="512"
        :disabled="disabled || !isEditing"
        hide-dropzone
        :show-max-filesize="evidenceSubmission.isNotSubmitted"
        :amplitude-attribute="{ Part: part, TaskNo: order }"
        @onFilesAdded="onFilesAdded"
        @file:removed="onFileRemoved"
        @upload:stopped="onUploadStopped"
        @upload:error="onUploadError"
        @upload:complete="onUploadComplete"
      >
      </base-s3-uploader>
      <b-button
        v-if="!readonly && mode !== 'review' && !isEditing"
        variant="outline-primary"
        class="mt-3"
        :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
        @click="enableEdit"
      >
        Edit evidence
      </b-button>

      <b-button
        v-if="!readonly && mode !== 'review' && isEditing"
        variant="success"
        class="mt-3"
        :disabled="isSaveButtonDisabled"
        :amplitude-attribute="JSON.stringify({ Part: part, TaskNo: order })"
        @click="saveEvidenceForLearner"
      >
        <b-spinner v-if="isProcessing" small /><span v-else>Save changes</span>
      </b-button>
    </div>
  </b-form>
</template>

<script>
import VueElementLoading from 'vue-element-loading';
import { kebabCase, isBoolean, isObject } from 'lodash';
import { notificationMixin } from '@/mixins/notificationMixin';
// import { useTextCounter } from "@/composables/useTextCounter";
import Post from '@/models/Post';
import PostResponse from '@/models/PostResponse';
import AssessmentEvaluation from '@/models/AssessmentEvaluation';
import Criteria from '@/models/Criteria';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseFilesList from '@/components/base-components/BaseFilesList';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
import CriteriaRadioGroup from '@/components/Post/CriteriaRadioGroup';
import Auth from '@/models/Auth';
import { useAuth } from '@/composables/useAuth';
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faLightbulb } from '@fortawesome/free-regular-svg-icons';

library.add(faLightbulb);



export default {
  components: {
    FontAwesomeIcon,
    BaseTextEditor,
    BaseFilesList,
    BaseS3Uploader,
    VueElementLoading,
    CriteriaRadioGroup
  },
  mixins: [amplitudeMixin, notificationMixin, userInfoMixin],

  props: {
    post: {
      type: Post,
      required: true
    },

    order: {
      type: Number,
      default: 0
    },

    criteria: {
      type: Array[Criteria],
      default: () => []
    },

    evidenceSubmission: {
      type: EvidenceSubmission,
      required: true
    },

    postResponse: {
      type: PostResponse,
      required: true
    },

    disabled: {
      type: [Number, Boolean],
      default: false
    },

    readonly: {
      type: [Number, Boolean],
      default: false
    },

    allowAssess: {
      type: Boolean,
      default: false
    },

    headerTitle: {
      type: String,
      default: 'Response'
    },

    mode: {
      type: String,
      default: ''
    },

    evidenceSubmitting: {
      type: Boolean,
      default: false
    },

    hideComment: {
      type: Boolean,
      default: false
    },

    part: {
      type: String,
      default: ''
    },

    showAllCriteria: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      saving: false,
      savingError: false,
      isInvalid: false,
      saveMessage: '',
      selected:
        this.postResponse &&
        this.postResponse.assessment_evaluation &&
        isBoolean(this.postResponse.assessment_evaluation.pass)
          ? {
              pass: this.postResponse.assessment_evaluation.pass,
              additional_criterion: isObject(
                this.postResponse.assessment_evaluation.additional_criterion
              )
                ? this.postResponse.assessment_evaluation.additional_criterion.id
                : this.postResponse.assessment_evaluation.additional_criterion
            }
          : null,
      giveFeedback: false,
      description: this.postResponse ? this.postResponse.description : '',
      formDescription: this.postResponse ? this.postResponse.description : '',
      formAttachments:
        this.postResponse && this.postResponse.attachments && this.postResponse.attachments.length
          ? this.postResponse.attachments
          : [],
      changing: false,
      onChanges: false,
      isLoadingPermission: false,
      message_th: '',
      message_en: '',
      selected_criteria_title: '',
      toDeleteFiles: [],
      isEditing: false,
      originalAttachments: [], // Track original attachments when editing starts
      uploaderReady: false, // Track when uploader ref is available
      hasPendingUploads: false, // Track if uploader has queued files (reactive)
      generating: false, // Track AI generation loading state
      isAIGenerated: false, // Track if current comment was generated by AI
      aiGenerated: false,
      aiHighlight: false, // Track if AI has been used (persists until page refresh)
      aiCommentSnapshot: null, // Snapshot of AI comment for comparison
      hasInteracted: false // Track if use has interacted with the form (for Assessor save button)
    };
  },

  computed: {
    formatedPart() {
      return kebabCase(this.part.toLowerCase());
    },

    referenceMeta() {
      return {
        ref: 'post-response',
        refId: this.postResponse ? this.postResponse.id : null
      };
    },

    // countTextDescription() {
    //   return countText(this.formDescription);
    // },

    // formDescription: {
    //   get() {
    //     if (this.mode === 'review' && !this.readonly) {
    //       return this.description;
    //     } else if (this.postResponse) {
    //       return this.postResponse.description;
    //     } else {
    //       return '';
    //     }
    //   },

    //   set(description) {
    //     if (this.mode === 'review') {
    //       this.description = description;
    //     } else {
    //       this.description = description;
    //     }
    //   }
    // },

    // formAttachments: {
    //   get() {
    //     return this.postResponse &&
    //       this.postResponse.attachments &&
    //       this.postResponse.attachments.length
    //       ? this.postResponse.attachments
    //       : [];
    //   },

    //   set(attachments) {
    //     PostResponse.update({
    //       where: this.postResponse.id,
    //       data: {
    //         attachments
    //       }
    //     });
    //   }
    // },

    variantAlert() {
      return this.selected && this.selected.pass === true
        ? 'info'
        : this.selected.pass === false && this.selected.additional_criterion
        ? 'warning'
        : 'danger';
    },

    variantIcon() {
      return this.selected.pass === true
        ? 'icon__info'
        : this.selected.pass === false && this.selected.additional_criterion
        ? 'icon__warning'
        : 'icon__danger';
    },

    isAssessor() {
      return this.evidenceSubmission ? this.evidenceSubmission.isAssessor(this.$currentUser.id) : false;
    },

    /**
     * Check if the current user is a learner
     * @returns {boolean}
     */
    isLearner() {
      const roles = this.$currentUser.user_roles;

      return roles.length > 0 && roles.every((roleObj) => roleObj.role === 'learner');
    },

    /**
     * Determine if the save button should be disabled for learner evidence submission
     * @returns {boolean}
     */
    isSaveButtonDisabled() {
      // Disable if currently saving or uploading
      if (this.isProcessing) return true;

      // For learner evidence submission: allow saving with EITHER description OR attachments
      // As per UI hint: "(Enter your answer here or upload a file.)"
      const hasDescription = this.formDescription && this.formDescription.trim() !== '';

      // Check for attachments (including pending uploads)
      const hasAttachments =
        (this.formAttachments && this.formAttachments.length > 0) || this.hasPendingUploads;

      // Disable save button only if user has NEITHER description NOR attachments (including pending)
      return !hasDescription && !hasAttachments;
    },

    isDirty() {
      // Check description
      const currentDesc = (this.formDescription || '').trim();
      const savedDesc = (this.postResponse?.description || '').trim();
      if (currentDesc !== savedDesc) return true;

      // Check evaluation
      const savedEval = this.postResponse?.assessment_evaluation;
      if (!savedEval) {
         // If saved is null, but selected is not null -> Dirty
         return this.selected !== null;
      }
      if (!this.selected) return true; // Saved exists, current is null -> Dirty

      if (this.selected.pass !== savedEval.pass) return true;

      const currentAddId = this.selected.additional_criterion;
      const savedAddId = isObject(savedEval.additional_criterion) ? savedEval.additional_criterion.id : savedEval.additional_criterion;
      
      // Handle null vs undefined or type mismatch
      if ((currentAddId || null) !== (savedAddId || null)) return true;

      return false;
    },

    hasSavedData() {
       const hasDescription = !!(this.postResponse?.description && this.postResponse.description.trim());
       const hasEvaluation = !!(this.postResponse?.assessment_evaluation && isBoolean(this.postResponse.assessment_evaluation.pass));
       return hasDescription && hasEvaluation;
    },

    isAssessorSaveDisabled() {
       if (this.mode !== 'assess') return false; // Should not trigger for others
       if (this.changing) return true;
       
       // Enable if not saved, or if dirty. Disable ONLY if saved AND clean.
       if (!this.hasSavedData) return false;
       return !this.isDirty;
    },

    isAIDisabledByPartDependency() {
      if (this.part !== 'REFLECTION') return false;

      const learningUnitId = this.evidenceSubmission?.learning_unit_id;
      if (!learningUnitId) return true;

      const partsToCheck = ['OVERVIEW', 'WORK_EXAMPLE'];

      for (const partKey of partsToCheck) {
        const posts = Post.query()
          .where('part', partKey)
          .where('learning_unit_id', learningUnitId)
          .where('post_type', 'EVALUATION_ASSESSMENT')
          .get();

        if (!posts.length) return true;

        for (const post of posts) {
          const postResponse = PostResponse.query()
            .where('evidence_submission_id', this.evidenceSubmission?.id)
            .where('post_id', post.id)
            .where('assessment_evaluation_id', (value) => value !== null)
            .with('assessment_evaluation')
            .first();

          if (
            !postResponse ||
            !postResponse.assessment_evaluation ||
            !isBoolean(postResponse.assessment_evaluation.pass) ||
            postResponse.is_editing
          ) {
            return true;
          }
        }
      }

      return false;
    },

    isProcessing() {
      return this.saving || this.changing;
    }
  },

  watch: {
    isProcessing: {
      handler(newVal) {
        this.$emit('processing', newVal);
      },
      immediate: true
    },

    isEditing: {
      handler(newVal) {
        this.$emit('editing', newVal);
        if (newVal) {
          // When editing is enabled, check for uploader availability
          this.$nextTick(() => {
            this.checkUploaderReady();
          });
        } else {
          // When editing is disabled, reset uploader state
          this.uploaderReady = false;
          this.hasPendingUploads = false;
        }
      },
      immediate: true
    },

    formDescription: {
      handler(value) {
        // Hide AI badge if user edits the comment manually
        const cleanValue = (value || '').replace(/<[^>]*>/g, '').trim();
        const cleanSnapshot = (this.aiCommentSnapshot || '').replace(/<[^>]*>/g, '').trim();

        if (this.isAIGenerated && this.aiCommentSnapshot !== null && cleanValue !== cleanSnapshot) {
          this.isAIGenerated = false;
        }

        // In assess mode, mark as editing only when content actually changes from saved value
        if (this.mode === 'assess') {
          const savedDesc = (this.postResponse?.description || '').trim();
          const currentDesc = (value || '').trim();
          if (currentDesc !== savedDesc) {
            PostResponse.update({
              where: this.postResponse.id,
              data: { is_editing: true }
            });
          } else if (!this.isDirty) {
            PostResponse.update({
              where: this.postResponse.id,
              data: { is_editing: false }
            });
          }
        }
      }
    },
    formAttachments: {
      handler(value) {
        console.log('[PostResponseForm] formAttachments changed:', value);
      },
      deep: true // Ensure deep watching for array changes
    },

    'postResponse.assessment_evaluation': {
      handler(newVal, oldVal) {
        // Prevent overwrite if value hasn't changed (deep equality check)
        // This validation is crucial to prevent AI generated selection being overwritten 
        // by is_editing updates or other non-evaluation changes in postResponse
        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
          return;
        }

        this.selected =
          newVal &&
          isBoolean(newVal.pass)
            ? {
                pass: newVal.pass,
                additional_criterion: newVal.additional_criterion
              }
            : null;

        if (this.selected) {
          const { additional_criterion: addCriterionId, pass } = this.selected;

          if (addCriterionId) {
            //additional criterion
            const addCriterion = this.criteria
              ?.flatMap((criterion) => criterion.additional_criteria || [])
              .find((item) => item.id === addCriterionId)?.title;

            if (addCriterion) this.displayMessage(addCriterion);
          } else {
            //normal criterion
            const normCriterion = this.criteria?.find((criterion) => {
              const criterionId = newVal?.criterion;
              return typeof criterionId === 'object'
                ? criterion.id === criterionId.id
                : criterion.id === criterionId;
            });

            if (normCriterion) {
              this.displayMessage(pass ? normCriterion.pass_title : normCriterion.not_yet_title);
            }
          }
        }
      },
      deep: true
    },
    'postResponse.id': {
      handler() {
        // Force the description and attachments to update when the ID changes
        this.description = this.postResponse ? this.postResponse.description : '';
        this.formDescription = this.postResponse ? this.postResponse.description : '';
        this.formAttachments =
          this.postResponse && this.postResponse.attachments && this.postResponse.attachments.length
            ? this.postResponse.attachments
            : [];

        // For learner state (no criteria), automatically enable editing if both description and files are empty
        const hasDescription = this.formDescription && this.formDescription.trim() !== '';
        const hasFiles = this.formAttachments && this.formAttachments.length > 0;
        const isLearnerState = !this.criteria || this.criteria.length === 0;

        if (isLearnerState && !hasDescription && !hasFiles) {
          this.isEditing = true;
          PostResponse.update({
            where: this.postResponse.id,
            data: { is_editing: true }
          });
        } else {
          this.isEditing = false;
          PostResponse.update({
            where: this.postResponse.id,
            data: { is_editing: false }
          });
        }

        this.saveMessage = '';
        this.savingError = false;
        this.saving = false;

        // Reset original attachments when switching posts
        this.originalAttachments = [];
        this.toDeleteFiles = [];

        if (this.mode === 'assess' && !this.readonly) {
          this.giveFeedback = true;
          this.isEditing = true;
        }
      },
      immediate: true // Run immediately when component is created
    },


  },


  mounted() {
    // Initial check for uploader ref (might not be available if isEditing is false)
    this.checkUploaderReady();
  },

  methods: {
    checkUploaderReady() {
      console.log('[PostResponseForm] checkUploaderReady called');
      console.log(
        `[PostResponseForm] isEditing: ${this.isEditing}, attachmentUploader ref:`,
        this.$refs.attachmentUploader
      );

      if (this.isEditing && this.$refs.attachmentUploader) {
        this.uploaderReady = true;
        this.updatePendingUploadsStatus();
        console.log('[PostResponseForm] Uploader ready and status updated');
      } else {
        console.log(
          '[PostResponseForm] Uploader not ready - isEditing:',
          this.isEditing,
          'ref exists:',
          !!this.$refs.attachmentUploader
        );
      }
    },

    updatePendingUploadsStatus() {
      console.log('[PostResponseForm] updatePendingUploadsStatus called');
      console.log(
        `[PostResponseForm] uploaderReady: ${this.uploaderReady}, attachmentUploader ref:`,
        this.$refs.attachmentUploader
      );

      // Update reactive state for pending uploads
      if (this.uploaderReady && this.$refs.attachmentUploader) {
        console.log('[PostResponseForm] uploaderReady and attachmentUploader ref found');

        const uploader = this.$refs.attachmentUploader;
        // Check if uploader has pending files - try both property and method
        let hasPending = false;
        try {
          if (typeof uploader.hasAnyQueuedFiles === 'function') {
            hasPending = uploader.hasAnyQueuedFiles();
          } else if (typeof uploader.hasAnyQueuedFiles === 'boolean') {
            hasPending = uploader.hasAnyQueuedFiles;
          } else if (uploader.files && uploader.files.length > 0) {
            // Fallback: check if files array has items
            hasPending = uploader.files.some((file) => file.status !== 'uploaded');
          }
        } catch (error) {
          console.warn('[PostResponseForm] Error checking uploader status:', error);
          hasPending = false;
        }

        this.hasPendingUploads = hasPending;
        console.log(`[PostResponseForm] hasPendingUploads updated: ${this.hasPendingUploads}`);
      } else {
        this.hasPendingUploads = false;
        console.log(
          '[PostResponseForm] No uploader ref available, setting hasPendingUploads to false'
        );
      }
    },

    openFileBrowser() {
      this.$nextTick(() => {
        this.$refs.attachmentUploader.openFileBrowser();
      });
    },

    onUploadComplete(attachments) {
      const { uploading_upload_ids = [] } = this.postResponse;

      // Remove completed upload IDs from the uploading list
      attachments.forEach((attachment) => {
        const index = uploading_upload_ids.indexOf(attachment.upload.uuid);
        if (index !== -1) {
          uploading_upload_ids.splice(index, 1);
        }
      });

      PostResponse.update({
        where: this.postResponse.id,
        data: {
          // attachments: [...this.formAttachments, ...attachments],
          uploading_upload_ids
        }
      });

      // Update pending uploads status
      this.$nextTick(() => {
        this.updatePendingUploadsStatus();
      });
    },
    async handleFile() {
      let updatedAttachments = this.formAttachments;
      if (this.$refs.attachmentUploader.hasAnyDeletedFiles()) {
        await this.$refs.attachmentUploader.deleteRemovedFiles();
      }

      if (this.$refs.attachmentUploader.hasAnyQueuedFiles()) {
        const uploadedFiles = await this.$refs.attachmentUploader.upload();
        console.log('[PostResponseForm] Files uploaded successfully', uploadedFiles);
        updatedAttachments = [...updatedAttachments, ...uploadedFiles];
      }
      if (this.toDeleteFiles.length) {
        // Clean up any null values before processing deletions
        this.cleanupToDeleteFiles();

        if (this.toDeleteFiles.length) {
          console.log('[PostResponseForm] Deleting attachments:', this.toDeleteFiles);
          updatedAttachments = this.formAttachments.filter(
            (attachment) =>
              !this.toDeleteFiles.includes(attachment.upload?.id) &&
              !this.toDeleteFiles.includes(attachment.id)
          );
        }
        this.toDeleteFiles = [];
      }
      await PostResponse.update({
        where: this.postResponse.id,
        data: {
          attachments: updatedAttachments
        }
      });

      return updatedAttachments;
    },

    async saveEvidenceForLearner() {
      try {
        this.saving = true;
        this.savingError = false;
        await this.saveDescription(this.formDescription, 0);
        
        // saveDescription internally sets this.saving back to false in its finally block.
        // We must re-assert it to true here so the upload phase keeps the button disabled.
        this.saving = true;

        const updatedAttachments = await this.handleFile();

        // Clear uploading_upload_ids and error_upload_ids after successful save
        // This ensures cleanup even if there were uploads in progress when saving
        PostResponse.update({
          where: this.postResponse.id,
          data: {
            uploading_upload_ids: [],
            error_upload_ids: []
          }
        });

        this.isEditing = false;
        PostResponse.update({
          where: this.postResponse.id,
          data: { is_editing: false }
        });

        this.$toast.success('Evidence saved successfully.');

        // Emit success event with updated data
        this.$emit('save-success', {
          post_id: this.postResponse.post_id,
          description: this.formDescription,
          attachments: updatedAttachments
        });
      } catch (error) {
        console.error('[PostResponseForm] Error saving evidence:', error);
        this.savingError = true;
      } finally {
        this.saving = false;
      }
    },

    enableEdit() {
      // Store the original attachments when editing starts
      this.originalAttachments = this.formAttachments ? [...this.formAttachments] : [];
      this.isEditing = true;
      PostResponse.update({
        where: this.postResponse.id,
        data: { is_editing: true }
      });
    },

    retrySave() {
      this.saveDescription(this.formDescription);
    },

    async saveDescription(description, timeoutTime = 1000) {
      this.savingError = false;
      this.saveMessage = 'Saving';
      this.saving = true;

      const handleSaveMessage = (message) => {
        if (timeoutTime === 0) {
          this.saveMessage = message;
        } else {
          setTimeout(() => {
            this.saveMessage = message;
          }, timeoutTime);
        }
      };

      const handleSavingState = (state) => {
        if (timeoutTime === 0) {
          this.saving = state;
        } else {
          setTimeout(() => {
            this.saving = state;
          }, timeoutTime);
        }
      };

      try {
        if (this.allowAssess) {
          await PostResponse.api().updatePostResponseByAssessor(this.postResponse.id, {
            description
          });
        } else {
          await PostResponse.api().updatePostResponse(this.postResponse.id, {
            description
          });
        }

        handleSaveMessage('Saved');
        PostResponse.update({
          where: this.postResponse.id,
          data: {
            description
          }
        });
      } catch (error) {
        console.log(error);

        // Handle network errors
        if (error?.message === 'Network Error') {
          this.$toast.error('Connection failed. Please try again.');
        }

        if (error.response.status === 403) {
          this.$toast.error('You don’t have permission to evaluate this submission.');

          return this.$router.push({
            name: 'design-planning/info',
            params: { learningUnitId: this.evidenceSubmission.learning_unit_id },
            query: { tab: 'assess' }
          });
        }

        handleSaveMessage('Save Failed');
        this.savingError = true;
      } finally {
        handleSavingState(false);

        PostResponse.update({
          where: this.postResponse.id,
          data: {
            save_message: this.saveMessage
          }
        });
      }
    },
    async isHavePermission() {
      const canFeedback = await EvidenceSubmission.api().canFeedback(this.evidenceSubmission.id);
      if (!canFeedback) {
        this.$toast.error('You don’t have permission to evaluate this submission.');
        return this.$router.push({
          name: 'design-planning/info',
          params: { learningUnitId: this.evidenceSubmission.learning_unit_id },
          query: { tab: 'assess' }
        });
      }
    },

    async onSubmit() {
      this.onChanges = true;

      if (this.selected === null || this.selected.pass === null || !this.formDescription) {
        if (this.selected === null || this.selected.pass === null) {
          this.notiError('Please select the assessment rubric.');
        }

        if (!this.formDescription) {
          this.notiError('Please insert the comment.');
        }
        return;
      }

      try {
        this.changing = true;

        if (this.allowAssess && !this.userRole.some((roleObj) => roleObj.role === 'issuer')) {
          await PostResponse.api().updatePostResponseByAssessor(this.postResponse.id, {
            description: this.formDescription
          });
        } else {
          if (this.postResponse.description !== this.formDescription) {
            await PostResponse.api().updatePostResponse(this.postResponse.id, {
              description: this.formDescription
            });
          }
        }

        if (this.hasAssessmentChanged()) {
          const body = {
            post_id: this.post.id,
            ...this.selected
          };

          await AssessmentEvaluation.api().update(this.postResponse.assessment_evaluation_id, body);
        }

        PostResponse.update({
          where: this.postResponse.id,
          data: {
            description: this.formDescription,
            save_message: this.saveMessage
          }
        });

        AssessmentEvaluation.update({
          where: this.postResponse.assessment_evaluation_id,
          data: {
            ...this.selected,
            additional_criterion: isObject(this.selected.additional_criterion)
              ? this.selected.additional_criterion.id
              : this.selected.additional_criterion
          }
        });

        PostResponse.update({
          where: this.postResponse.id,
          data: { is_editing: false }
        });

        if (this.mode !== 'assess') {
          this.giveFeedback = false;
        }
        this.isAIGenerated = false;
        this.$toast.success('Evaluation saved successfully.');
      } catch (error) {
        console.log(error);
        this.onClickCancel();
        if (error.response.status === 403) {
          this.$toast.error('You don’t have permission to evaluate this submission.');
          return this.$router.push({
            name: 'design-planning/info',
            params: { learningUnitId: this.evidenceSubmission.learning_unit_id },
            query: { tab: 'assess' }
          });
        }
      } finally {
        this.changing = false;
      }

      if (this.mode !== 'assess') {
        this.giveFeedback = false;
      } else {
        this.hasInteracted = false;
      }
    },

    onClickCancel() {
      this.description = this.postResponse ? this.postResponse.description : '';
      this.selected =
        this.postResponse &&
        this.postResponse.assessment_evaluation &&
        isBoolean(this.postResponse.assessment_evaluation.pass)
          ? {
              pass: this.postResponse.assessment_evaluation.pass,
              additional_criterion: this.postResponse.assessment_evaluation.additional_criterion
                ? this.postResponse.assessment_evaluation.additional_criterion
                : null
            }
          : null;

      this.onChanges = false;
      this.isAIGenerated = false;
      if (this.mode !== 'assess') {
        PostResponse.update({
          where: this.postResponse.id,
          data: { is_editing: false }
        });
        this.giveFeedback = false;
      } else {
        this.hasInteracted = false;
      }
    },

    onFilesAdded(fileList) {
      const { uploading_upload_ids = [] } = this.postResponse;

      [...fileList].forEach((file) => {
        if (file.status !== 'error') uploading_upload_ids.push(file.upload.uuid);
      });

      PostResponse.update({
        where: this.postResponse.id,
        data: { uploading_upload_ids }
      });

      // Update pending uploads status
      this.$nextTick(() => {
        this.updatePendingUploadsStatus();
      });
    },

    onFileRemoved(removedFile) {
      // Only add to toDeleteFiles if the file has a valid id (i.e., it was previously saved)
      if (removedFile.id && removedFile.id !== null && removedFile.id !== undefined) {
        this.toDeleteFiles.push(removedFile.id);
      }

      // Update pending uploads status
      this.$nextTick(() => {
        this.updatePendingUploadsStatus();
      });
    },

    // Helper method to clean up any null values in toDeleteFiles
    cleanupToDeleteFiles() {
      this.toDeleteFiles = this.toDeleteFiles.filter((id) => id !== null && id !== undefined);
    },

    onUploadStopped(file) {
      const { error_upload_ids = [] } = this.postResponse;

      error_upload_ids.push(file.upload.uuid);

      PostResponse.update({
        where: this.postResponse.id,
        data: { error_upload_ids }
      });
    },

    onUploadError(file) {
      const { uploading_upload_ids = [], error_upload_ids = [] } = this.postResponse;

      const matchedUploadingIndex = uploading_upload_ids.indexOf(file.upload.uuid);

      if (matchedUploadingIndex !== -1) {
        uploading_upload_ids.splice(matchedUploadingIndex, 1);
      }

      error_upload_ids.push(file.upload.uuid);

      PostResponse.update({
        where: this.postResponse.id,
        data: { uploading_upload_ids, error_upload_ids }
      });

      // Update pending uploads status
      this.$nextTick(() => {
        this.updatePendingUploadsStatus();
      });
    },

    onRadioChange(event, { additional_criterion = null, criterion = null } = {}) {
      const {
        _value: { pass }
      } = event.target;
      let criteria = '';

      if (additional_criterion !== null) {
        criteria = additional_criterion.title;
      } else {
        if (pass) {
          criteria = criterion ? criterion.pass_title : 'Pass';
        } else {
          criteria = criterion ? criterion.not_yet_title : 'Not Yet';
        }
      }

      this.displayMessage(criteria);

      this.$_amplitudeMixin_onChange(event.target, {
        TaskNo: this.order,
        Part: this.part,
        Criteria: criteria,
        Value: event.target._value
      });
    },

    onEditorFocus() {

      if (this.mode !== 'assess') {
        PostResponse.update({
          where: this.postResponse.id,
          data: { is_editing: true }
        });
      }
      this.giveFeedback = true;
      if (this.mode === 'assess') {
        this.hasInteracted = true;
      }
    },

    async onGiveFeedback() {
      PostResponse.update({
        where: this.postResponse.id,
        data: { is_editing: true }
      });
      this.giveFeedback = true;
    },

    async onGenerateWithAI() {
      if (this.mode !== 'assess') return;

      // If text editor already has content, confirm before replacing
      if (this.formDescription && this.formDescription.replace(/<[^>]*>/g, '').trim()) {
        const confirmed = await this.$bvModal.msgBoxConfirm('Using the AI generate will replace all text currently in the comment box. Would you like to continue?', {
          title: 'Confirm AI Generation',
          okVariant: 'primary',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        });

        if (!confirmed) return;
      }

      await this._executeAIGenerate();
    },

    /**
     * Performs the ai-verify request with the same token-refresh behaviour as the
     * axios interceptor used by the other endpoints. Because this is a raw `fetch`
     * (needed for SSE streaming) it doesn't pass through that interceptor, so we:
     *   1. proactively refresh the token if it has already expired, and
     *   2. refresh + retry once if the server still responds with 401.
     */
    async fetchAIVerify(payload) {
      const { refreshToken } = useAuth();

      const sendRequest = () => {
        const token = Auth.getAppToken();
        return fetch(
          `${process.env.VUE_APP_CATALOG_API_URL}/v1/evidence-submissions/ai-verify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify(payload)
          }
        );
      };

      // Proactively refresh an already-expired token before sending.
      if (Auth.isTokenExpired()) {
        await refreshToken();
      }

      let response = await sendRequest();

      // Retry once on 401 after refreshing, mirroring the axios interceptor.
      if (response.status === 401) {
        await refreshToken();
        response = await sendRequest();
      }

      return response;
    },

    async _executeAIGenerate() {
      const originalDescription = this.formDescription;
      try {
        this.generating = true;

        const payload = {
          learning_unit_id: this.evidenceSubmission.learning_unit_id,
          user_id: this.evidenceSubmission.user_id,
          post_id: this.post.id,
          comment: this.formDescription ? this.formDescription.replace(/<[^>]*>/g, '') : this.formDescription
        };

        const criteriaStr = this.selectedCriteriaToString();
        if (criteriaStr) {
          payload.criteria = criteriaStr;
        }

        const response = await this.fetchAIVerify(payload);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'AI generation failed, please try again.');
        }

        // Enable feedback mode before streaming begins
        PostResponse.update({ where: this.postResponse.id, data: { is_editing: true } });
        this.giveFeedback = true;
        if (this.mode === 'assess') {
          this.hasInteracted = true;
        }

        this.formDescription = '';
        let accumulatedComment = '';
        let criteriaResolved = false;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let reading = true;
        while (reading) {
          const { done, value } = await reader.read();
          if (done) { reading = false; break; }

          buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by double newlines
          const parts = buffer.split('\n\n');
          buffer = parts.pop(); // keep the incomplete trailing chunk

          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith('data: ')) continue;

            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const event = JSON.parse(data);

              if (event.type === 'criteria' && !criteriaResolved) {
                const hadCriteriaSelected =
                  this.selected &&
                  (this.selected.pass === true || this.selected.pass === false);
                if (!hadCriteriaSelected) {
                  this.selected = this.parseAICriteria({ criteria: event.value });
                }
                criteriaResolved = true;
              } else if (event.type === 'delta') {
                accumulatedComment += event.text;
                // Sanitize on each chunk — DOMPurify auto-closes open tags so Quill
                // always receives valid HTML and the content renders progressively.
                this.formDescription = this.$sanitize(accumulatedComment);
              } else if (event.type === 'error') {
                throw new Error(event.message || 'AI generation failed, please try again.');
              }
            } catch {
              // ignore malformed SSE events
            }
          }
        }

        if (!accumulatedComment) {
          throw new Error('AI generation failed, please try again.');
        }

        // Final sanitized HTML — the snapshot stores the clean HTML so the
        // formDescription watcher (which strips tags for comparison) works correctly.
        const sanitizedHtml = this.$sanitize(accumulatedComment);
        this.formDescription = sanitizedHtml;
        this.aiCommentSnapshot = sanitizedHtml;
        this.isAIGenerated = true;
        this.aiGenerated = true;

        this.aiHighlight = true;
        setTimeout(() => {
          this.aiHighlight = false;
        }, 5000);
      } catch (error) {
        console.error('[PostResponseForm] AI generation failed:', error);
        this.formDescription = originalDescription;
        const errorMessage = error?.message || 'AI generation failed, please try again.';
        this.$toast.error(errorMessage);
      } finally {
        this.generating = false;
      }
    },

    selectedCriteriaToString() {
      if (!this.selected || this.selected.pass === null || this.selected.pass === undefined) {
        return null;
      }

      const criterion = this.criteria?.[0];
      if (!criterion) return null;

      if (this.selected.additional_criterion) {
        const match = this.criteria
          .flatMap((c) => c.additional_criteria || [])
          .find((ac) => String(ac.id) === String(this.selected.additional_criterion));
        return match?.title || null;
      }

      return this.selected.pass ? criterion.pass_title : criterion.not_yet_title;
    },

    parseAICriteria(result) {
        let pass = false;
        let additionalId = null;

        if (result.criteria) {
          // New format: "Pass", "Not Yet", or Additional Criterion Name/ID
          const criteriaVal = String(result.criteria).trim();
          
          let isPassTitle = false;
          let isNotYetTitle = false;
          
          if (this.criteria && this.criteria.length) {
            isPassTitle = this.criteria.some(c => c.pass_title && String(c.pass_title).trim().toLowerCase() === criteriaVal.toLowerCase());
            isNotYetTitle = this.criteria.some(c => c.not_yet_title && String(c.not_yet_title).trim().toLowerCase() === criteriaVal.toLowerCase());
          }

          if (criteriaVal.toLowerCase() === 'pass' || isPassTitle) {
            pass = true;
          } else if (criteriaVal.toLowerCase() === 'not yet' || isNotYetTitle) {
            pass = false;
          } else {
            // Check if it matches an additional criterion
            pass = false;
            let found = false;
            
            if (this.criteria && this.criteria.length) {
              for (const c of this.criteria) {
                if (c.additional_criteria && c.additional_criteria.length) {
                  const match = c.additional_criteria.find(
                    (ac) =>
                      String(ac.title).trim().toLowerCase() === criteriaVal.toLowerCase() ||
                      String(ac.id) === criteriaVal
                  );
                  if (match) {
                    additionalId = match.id;
                    found = true;
                    break;
                  }
                }
              }
            }
            
            if (!found) {
               console.warn('[PostResponseForm] Could not map criteria:', criteriaVal);
            }
          }
        } else {
          // Fallback to old format (pass boolean + additional_criterion)
          pass = result.pass === true || String(result.pass) === 'true';
          additionalId = isObject(result.additional_criterion)
            ? result.additional_criterion.id
            : result.additional_criterion || null;
        }

        return {
          pass,
          additional_criterion: additionalId
        };
    },

    async displayMessage(criteria) {
      const messages = {
        pass: {
          th: 'และกล่าวความเห็นในเชิงบวก ข้อดีและจุดแข็งต่อชิ้นงาน ดังตัวอย่างประโยคนี้',
          en: 'This is a very good and informative answer. You’ve provided the necessary context about your classroom.'
        },
        notYet: {
          th: 'แล้วสอบถามหรือขอข้อมูลเพิ่มเติมในประเด็นที่ควรได้รับการแก้ไข เพื่อให้ถูกต้องตามเกณฑ์ที่กำหนด ดังตัวอย่างประโยคนี้',
          en: 'Could you please provide further clarification on your answer to the last question?'
        }
      };

      const isPass = this.selected?.pass === true;
      const messageType = isPass ? 'pass' : 'notYet';

      this.message_th = messages[messageType].th;
      this.message_en = messages[messageType].en;
      this.selected_criteria_title = criteria;
    },
    hasAssessmentChanged() {
      const current = this.postResponse.assessment_evaluation;

      return (
        this.selected.pass !== current.pass ||
        this.selected.additional_criterion !== current.additional_criterion
      );
    },

    // Methods for parent components to control form state
    async forceSaveAndDisable() {
      if (this.isEditing) {
        await this.saveEvidenceForLearner();
      }
    },

    forceResetEdit() {
      if (this.isEditing) {
        this.isEditing = false;
        PostResponse.update({
          where: this.postResponse.id,
          data: { is_editing: false }
        });
        // Reset form to original values
        this.formDescription = this.postResponse ? this.postResponse.description : '';

        // Reset attachments to original state
        this.formAttachments = [];
        this.originalAttachments = [];

        this.saveMessage = '';
        this.savingError = false;
        this.saving = false;

        // Clear any pending file deletions - this is crucial!
        this.toDeleteFiles = [];
        this.cleanupToDeleteFiles(); // Extra safety to ensure no null values

        //reset file uploader state
        this.$nextTick(() => {
          this.resetFileUploader();
        });
      }
    },

    resetFileUploader() {
      try {
        const uploader = this.$refs.attachmentUploader;
        if (!uploader) {
          console.debug('[PostResponseForm] No file uploader found to reset');
          return;
        }

        // Use the uploader's built-in reset method if available
        if (typeof uploader.resetUploader === 'function') {
          console.debug("[PostResponseForm] Using uploader's built-in reset method");
          uploader.resetUploader();

          // Update pending uploads status after reset
          this.$nextTick(() => {
            this.updatePendingUploadsStatus();
          });
          return;
        }

        // Fallback to manual reset if the method doesn't exist
        console.debug('[PostResponseForm] Using fallback manual reset method');

        if (!uploader.$refs.vueDropzone) {
          console.debug('[PostResponseForm] No vue-dropzone found to reset');
          return;
        }

        const dropzone = uploader.$refs.vueDropzone.dropzone;
        if (!dropzone) {
          console.debug('[PostResponseForm] No dropzone instance found to reset');
          return;
        }

        // Remove all files from dropzone (queued, uploading, accepted, rejected)
        const allFiles = [
          ...dropzone.getQueuedFiles(),
          ...dropzone.getUploadingFiles(),
          ...dropzone.getAcceptedFiles(),
          ...dropzone.getRejectedFiles()
        ];

        console.debug('[PostResponseForm] Clearing', allFiles.length, 'files from dropzone');

        allFiles.forEach((file) => {
          try {
            dropzone.removeFile(file);
          } catch (error) {
            console.warn('[PostResponseForm] Error removing file from dropzone:', error);
          }
        });

        // Clear the buffer in the uploader component
        if (uploader.buffer) {
          console.debug(
            '[PostResponseForm] Clearing uploader buffer with',
            uploader.buffer.length,
            'items'
          );
          uploader.buffer = [];
        }

        // Reset any error states in the uploader
        if (typeof uploader.isErrorFileType !== 'undefined') {
          uploader.isErrorFileType = false;
        }
        if (typeof uploader.isErrorFileSize !== 'undefined') {
          uploader.isErrorFileSize = false;
        }

        console.debug('[PostResponseForm] File uploader reset completed');

        // Update pending uploads status after reset
        this.$nextTick(() => {
          this.updatePendingUploadsStatus();
        });
      } catch (error) {
        console.error('[PostResponseForm] Error during file uploader reset:', error);
      }
    },

    // Methods for parent components to control form state
    hasUnsavedChanges() {
      // Check if form is currently being edited (learner state only)
      if (this.isEditing) {
        // Check if description has actually changed from the original
        const originalDescription = this.postResponse ? this.postResponse.description : '';
        const currentDescription = this.formDescription || '';
        const descriptionChanged = originalDescription.trim() !== currentDescription.trim();

        // Check if attachments have actually changed from the original
        const originalAttachments =
          this.postResponse && this.postResponse.attachments ? this.postResponse.attachments : [];
        const currentAttachments = this.formAttachments || [];
        const attachmentsChanged =
          JSON.stringify(originalAttachments.map((a) => a.id).sort()) !==
          JSON.stringify(currentAttachments.map((a) => a.id).sort());

        // Check if there are files marked for deletion (filter out null values)
        const validFilesToDelete = this.toDeleteFiles
          ? this.toDeleteFiles.filter((id) => id !== null && id !== undefined)
          : [];
        const hasFilesToDelete = validFilesToDelete.length > 0;

        // Check if file uploader has pending files
        let hasPendingFiles = false;
        if (this.$refs.attachmentUploader) {
          const uploader = this.$refs.attachmentUploader;
          if (uploader.hasAnyQueuedFiles && uploader.hasAnyQueuedFiles()) {
            hasPendingFiles = true;
          }
        }
        console.log('hasUnsavedChanges', {
          descriptionChanged,
          attachmentsChanged,
          hasFilesToDelete,
          hasPendingFiles
        });

        return descriptionChanged || attachmentsChanged || hasFilesToDelete || hasPendingFiles;
      }

      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.readonly {
  .ql-container.ql-snow.ql-disabled {
    border-radius: 0.65rem !important;

    .ql-editor {
      border-radius: 0.65rem !important;
    }
  }
}

.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}

.text__feedback-text {
  display: flex;
  flex-direction: column;

  img,
  p {
    max-width: -moz-available; /* WebKit-based browsers will ignore this. */
    max-width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  }
}

.button__generate-ai {
  background: linear-gradient(135deg, #667ce8 0%, #744ba3 100%);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(102, 124, 232, 0.3);
  font-weight: 600;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    color: #ffffff;
  }

  &:focus,
  &:active {
    color: #ffffff !important;
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    color: #ffffff;
  }
}

.button__regenerate-ai {
  background: #ffffff;
  color: #3c44b1;
  border: 1.6px solid #3c44b1;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #eceef7;
    color: #3c44b1;
    border-color: #3c44b1;
  }

  &:focus,
  &:active {
    background: #ffffff;
    color: #3c44b1 !important;
    border-color: #3c44b1;
  }

  &:disabled {
    opacity: 0.6;
    color: #3c44b1;
  }
}

.badge__ai-generated {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eceef7;
  color: #3c44b1;
  border-radius: 160px;
  padding: 4px 12px;
  font-size: 12.8px;
  font-weight: 700;
  text-transform: uppercase;
}

.icon__ai-btn {
  width: 16px;
  height: 16px;
  vertical-align: -2px;
  margin-right: 4px;
}

.icon__ai-badge {
  width: 12px;
  height: 12px;
}

.text-editor__ai-highlight {
  position: relative;
  ::v-deep .ql-toolbar.ql-snow {
    border-top-color: transparent;
    border-left-color: transparent;
    border-right-color: transparent;
    border-radius: 4.64px 4.64px 0 0;
   }
  ::v-deep .ql-container.ql-snow {
    border-left-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
    border-radius: 0 0 4.64px 4.64px;
  }
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #667ce8 0%, #744ba3 100%);
    border-radius: 6px;
    z-index: 0;
    pointer-events: none;
  }

  ::v-deep .ql-toolbar.ql-snow,
  ::v-deep .ql-container.ql-snow {
    position: relative;
    z-index: 1;
    background: #fff;
  }
  
}
</style>
